import { configureStore } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/query/react'

import { host } from 'constants/apiconfig'
import {
    IARPlaybook,
    IARPlaybookTheme,
    IBusinessUser,
    IModel,
    IStudentBook,
} from 'modules/arplaybook/businessuser/types'
import { TRelationType } from 'modules/arplaybook/student/types'
import { IAuthSettings } from 'modules/arplaybook/types/auth'

import { arPlaybookAdminApi } from './arPlaybookAdminApi'
import { arPlaybookSuperAdminApi } from './arPlaybookSuperAdminApi'
import { marketplaceApi } from './marketplaceApi'
import {
    BUSINESS_USER_TOKEN,
    STUDENT_TOKEN,
    businessUserBaseQueryWithAuth,
    studentBaseQueryWithAuth,
} from './arPlaybookBaseQuery'
import {
    ICreateFileRequestArgs,
    ICreateFileResponse,
    IFileLinkRequestArgs,
    IFileLinkResponse,
} from './fileApi'

// Re-export for backward compatibility
export {
    BUSINESS_USER_TOKEN,
    STUDENT_TOKEN,
    businessUserBaseQueryWithAuth,
    studentBaseQueryWithAuth,
}

export interface IBusinessUserSignupRequest {
    name: string
    username: string
    password: string
    institutionName: string
    subdomain: string
    country?: string
}

export interface IBusinessUserGoogleSignupRequest {
    code: string
    name: string
    institutionName: string
    subdomain: string
    country?: string
}

export interface IBusinessUserGoogleLoginRequest {
    code: string
}

export interface IBusinessUserGoogleAuthResponse {
    token: string
    subdomain: string
    googleUser?: {
        name?: string
        email: string
    }
    needsSignup?: boolean
}

export interface IBusinessUserSetPasswordRequest {
    password: string
}

export interface IBusinessUserLoginRequest {
    username: string
    password: string
    remember?: boolean
}

export interface IAuthResponse {
    token: string
}

export interface ISendOTPRequest {
    phone: number
}

export interface IVerifyOTPRequest {
    phone: number
    otp: number
}

export interface IRegisterStudentRequest {
    studentName: string
    relationType: TRelationType
    relationName: string
    email: string
    phone: number
    licenseKey: string
}

export interface ILoginStudentRequest {
    licenseKey: string
}

export interface IDownloadLicensesRequest {
    quantity: number
    activationUrl: string
}

export interface IDownloadLicensesResponse {
    licenses: {
        licenseNo: number
        licenseUrl: string
    }[]
}

export interface ILicenseResponse {
    token: string
    book: string
}

/**
 * Response from getBook endpoint
 * Returns full book for licensed students, demo content for others
 */
export interface IStudentBookV2Response {
    /** Book content (full or demo depending on license) */
    book: IStudentBook
    /** Demo content for preview */
    demoContent?: IStudentBook
}

// Custom Domain Types
export enum CustomDomainStatus {
    PENDING_VERIFICATION = 'pending_verification',
    PENDING_SSL = 'pending_ssl',
    ACTIVE = 'active',
    ERROR = 'error',
}

export interface ICustomDomain {
    id: string
    domain: string
    status: CustomDomainStatus
    sslStatus?: string
    verificationToken: string
    verifiedAt?: string
    activatedAt?: string
    errorMessage?: string
    createdAt: string
    updatedAt: string
}

export interface IDNSInstructions {
    verification: {
        type: string
        name: string
        value: string
        ttl: string
    }
    cname: {
        type: string
        name: string
        value: string
        ttl: string
    }
}

export interface ICustomDomainResponse {
    success: boolean
    data: ICustomDomain & {
        dnsInstructions?: IDNSInstructions
    }
}

export interface ICustomDomainsListResponse {
    success: boolean
    data: ICustomDomain[]
}

export interface IVerifyDomainResponse {
    success: boolean
    data: {
        verified: boolean
        status: CustomDomainStatus
        sslStatus?: string
        message: string
    }
}

// Analytics Types
export interface IAnalyticsDateRange {
    startDate?: string
    endDate?: string
}

export interface IAnalyticsQuery extends IAnalyticsDateRange {
    bookId?: string
}

export interface IBookEngagement {
    bookId: string
    bookName: string
    totalSessions: number
    uniqueUsers: number
    totalPageViews: number
    avgSessionDuration: number
    arLaunches: number
}

export interface IPageEngagement {
    pageNumber: number
    pageWord: string
    views: number
    arLaunches: number
}

export interface ISessionData {
    date: string
    sessions: number
    users: number
}

export interface IDeviceData {
    device: string
    users: number
}

export interface IAnalyticsOverview {
    totalSessions: number | 'Error'
    uniqueUsers: number | 'Error'
    totalPageViews: number | 'Error'
    totalARLaunches: number | 'Error'
    avgSessionDuration: number | 'Error'
}

export interface IAREducationAnalytics {
    overview: IAnalyticsOverview
    bookEngagement: IBookEngagement[] | 'Error'
    sessionsOverTime: ISessionData[] | 'Error'
    deviceBreakdown: IDeviceData[] | 'Error'
    topPages: IPageEngagement[] | 'Error'
}

export interface ISuperAdminAnalytics {
    overview: IAnalyticsOverview
    sessionsOverTime: ISessionData[] | 'Error'
    deviceBreakdown: IDeviceData[] | 'Error'
}

// ============================================
// Marketplace Seller Types
// ============================================

export interface IRecordOfflineSaleRequest {
    bookId: string
    buyerEmail?: string
    buyerName?: string
    buyerPhone?: string
    amount?: number
    notes?: string
}

export interface IRecordOfflineSaleResponse {
    success: boolean
    message: string
    licenseKey: string
    activationUrl: string
    purchaseId: string
}

// ============================================
// AI Audio Generation Types
// ============================================

export enum TTSVoice {
    ALLOY = 'alloy',
    ECHO = 'echo',
    FABLE = 'fable',
    ONYX = 'onyx',
    NOVA = 'nova',
    SHIMMER = 'shimmer',
}

export enum TTSModel {
    TTS_1 = 'tts-1',
    TTS_1_HD = 'tts-1-hd',
}

export enum TTSOutputFormat {
    MP3 = 'mp3',
    OPUS = 'opus',
    AAC = 'aac',
    FLAC = 'flac',
}

export interface IGenerateAudioRequest {
    text: string
    voice?: TTSVoice
    model?: TTSModel
    speed?: number
    format?: TTSOutputFormat
    languageCode?: string
}

export interface IGenerateAudioResponse {
    audioUrl: string
    durationSeconds?: number
    voice: TTSVoice
    languageCode?: string
}

export interface ITTSVoiceInfo {
    id: TTSVoice
    name: string
    description: string
    default?: boolean
}

export interface IAvailableVoicesResponse {
    voices: ITTSVoiceInfo[]
    defaultVoice: TTSVoice
    speeds: {
        min: number
        max: number
        default: number
    }
    formats: TTSOutputFormat[]
    defaultFormat: TTSOutputFormat
    maxTextLength: number
}

// ============================================

export interface ISellerEarningsSummary {
    totalSales: number
    totalRevenue: number
    totalPlatformFees: number
    totalEarnings: number
    pendingPayout: number
    paidOut: number
    onlineSales: number
    offlineSales: number
}

export interface IRecentSale {
    _id: string
    bookName: string
    buyerEmail: string
    buyerName?: string
    amount: number
    currency: string
    sellerEarnings: number
    saleType: 'online' | 'offline'
    payoutStatus: string
    createdAt: string
}

export interface IMonthlyBreakdown {
    _id: { year: number; month: number }
    sales: number
    earnings: number
}

export interface ISellerEarningsResponse {
    summary: ISellerEarningsSummary
    recentSales: IRecentSale[]
    monthlyBreakdown: IMonthlyBreakdown[]
    platformFeePercent: number
    minimumPayout: number
    currency: string
}

export interface ISellerSalesQuery {
    page?: number
    limit?: number
    bookId?: string
    status?: string
    saleType?: string
}

export interface IMarketplaceSettingsResponse {
    settings: {
        title?: string
        description?: string
        featuredBookIds?: string[]
        heroBanner?: string
        socialLinks?: { platform: string; url: string }[]
    }
    sellerName: string
    marketplaceUrl: string
    publishedBookCount: number
}

export interface IUpdateMarketplaceSettingsRequest {
    title?: string | null
    description?: string | null
    featuredBookIds?: string[]
    heroBanner?: string | null
    socialLinks?: { platform: string; url: string }[]
}

export interface ISaleRecord {
    _id: string
    bookName: string
    licenseKey?: string
    buyerEmail: string
    buyerName?: string
    buyerPhone?: string
    amount: number
    currency: string
    sellerEarnings: number
    platformFeeAmount: number
    saleType: 'online' | 'offline'
    status: string
    payoutStatus: string
    notes?: string
    createdAt: string
}

export interface ISellerSalesResponse {
    sales: ISaleRecord[]
    pagination: {
        total: number
        page: number
        limit: number
        totalPages: number
    }
}

export const businessUserApi = createApi({
    reducerPath: 'businessUserApi',
    baseQuery: businessUserBaseQueryWithAuth,
    refetchOnMountOrArgChange: 30,
    tagTypes: [
        'BusinessUser',
        'Books',
        'Theme',
        'MODEL_ASSETS',
        'CustomDomains',
        'SellerEarnings',
        'SellerSales',
        'MarketplaceSettings',
    ],
    endpoints: builder => ({
        getAssetsLink: builder.query<IFileLinkResponse, IFileLinkRequestArgs>({
            query: ({ fileName, folderName = '', uniqueFolder = false }) => {
                const queryParams = new URLSearchParams()
                if (folderName) {
                    queryParams.append('folderName', folderName)
                }
                if (uniqueFolder) {
                    queryParams.append('uniqueFolder', uniqueFolder.toString())
                }
                const queryString = queryParams.toString()
                const requestUrl = `${host}ar-playbook/start-assets-upload/${fileName}${queryString ? `?${queryString}` : ''}`
                return requestUrl
            },
        }),

        createFile: builder.mutation<ICreateFileResponse, ICreateFileRequestArgs>({
            queryFn: async ({ file, url }) => {
                try {
                    const response = await fetch(url, {
                        method: 'PUT',
                        body: file,
                        headers: {
                            'Content-Type': file.type,
                        },
                    })

                    if (!response.ok) {
                        throw new Error(response.statusText)
                    }

                    return {
                        data: {
                            success: true,
                        },
                    }
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: `Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`,
                        },
                    }
                }
            },
        }),

        businessUserSignup: builder.mutation<IAuthResponse, IBusinessUserSignupRequest>({
            query: userData => ({
                url: `${host}ar-playbook/business-users/signup`,
                method: 'POST',
                body: userData,
            }),
        }),
        businessUserGoogleSignup: builder.mutation<
            IBusinessUserGoogleAuthResponse,
            IBusinessUserGoogleSignupRequest
        >({
            query: userData => ({
                url: `${host}ar-playbook/business-users/google-signup`,
                method: 'POST',
                body: userData,
            }),
        }),
        businessUserGoogleLogin: builder.mutation<
            IBusinessUserGoogleAuthResponse,
            IBusinessUserGoogleLoginRequest
        >({
            query: userData => ({
                url: `${host}ar-playbook/business-users/google-login`,
                method: 'POST',
                body: userData,
            }),
        }),
        businessUserSetPassword: builder.mutation<
            { message: string; hasPassword: boolean },
            IBusinessUserSetPasswordRequest
        >({
            query: data => ({
                url: `${host}ar-playbook/business-users/set-password`,
                method: 'POST',
                body: data,
            }),
        }),
        checkSubdomainAvailability: builder.mutation<
            { available: boolean; subdomain: string },
            { subdomain: string }
        >({
            query: ({ subdomain }) => ({
                url: `${host}ar-playbook/business-users/check-subdomain`,
                method: 'POST',
                body: { subdomain },
            }),
        }),
        businessUserLogin: builder.mutation<IAuthResponse, IBusinessUserLoginRequest>({
            query: ({ username, password, remember }) => {
                const base64Credentials = btoa(`${username}:${password}`)
                return {
                    url: `${host}ar-playbook/business-users/login`,
                    method: 'POST',
                    headers: {
                        Authorization: `Basic ${base64Credentials}`,
                        'Content-Type': 'application/json',
                    },
                    body: { remember },
                }
            },
        }),
        businessUserForgotPassword: builder.mutation<{ message: string }, { username: string }>({
            query: ({ username }) => ({
                url: `${host}ar-playbook/business-users/forgot-password`,
                method: 'POST',
                body: { username },
            }),
        }),
        businessUserResetPassword: builder.mutation<
            { message: string },
            { token: string; password: string }
        >({
            query: ({ token, password }) => ({
                url: `${host}ar-playbook/business-users/reset-password`,
                method: 'POST',
                body: { token, password },
            }),
        }),
        businessUserProfile: builder.query<IBusinessUser, void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/profile`,
                method: 'GET',
            }),
            providesTags: ['BusinessUser'],
        }),
        getTheme: builder.query<null | IARPlaybookTheme, void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/theme`,
                method: 'GET',
            }),
            providesTags: ['Theme'],
        }),
        updateTheme: builder.mutation<IARPlaybookTheme, IARPlaybookTheme>({
            query: body => ({
                url: `${host}ar-playbook/business-users/theme`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Theme'],
        }),
        updateAuthSettings: builder.mutation<IBusinessUser, IAuthSettings>({
            query: body => ({
                url: `${host}ar-playbook/business-users/auth-settings`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['BusinessUser'],
        }),
        updateCookieConsentSetting: builder.mutation<IBusinessUser, { showCookieConsent: boolean }>(
            {
                query: body => ({
                    url: `${host}ar-playbook/business-users/cookie-consent-setting`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['BusinessUser'],
            },
        ),
        getModelAssets: builder.query<IModel[], void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/model-assets`,
                method: 'GET',
            }),
            providesTags: ['MODEL_ASSETS'],
        }),

        // AI Audio Generation endpoints
        generateAudio: builder.mutation<IGenerateAudioResponse, IGenerateAudioRequest>({
            query: body => ({
                url: `${host}ar-playbook/business-users/generate-audio`,
                method: 'POST',
                body,
            }),
        }),
        getAvailableVoices: builder.query<IAvailableVoicesResponse, void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/audio-voices`,
                method: 'GET',
            }),
        }),

        createBook: builder.mutation<IARPlaybook, IARPlaybook>({
            query: body => ({
                url: `${host}ar-playbook/business-users/books`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Books'],
        }),
        getAllBooks: builder.query<IARPlaybook[], void>({
            query: () => {
                return {
                    url: `${host}ar-playbook/business-users/books`,
                    method: 'GET',
                }
            },
            providesTags: ['Books'],
        }),
        publishBook: builder.mutation<
            { message: string; format: string; encrypted: boolean },
            string
        >({
            query: bookId => ({
                url: `${host}ar-playbook/business-users/books/${bookId}/publish`,
                method: 'POST',
            }),
            invalidatesTags: ['Books'],
        }),
        getBook: builder.query<IARPlaybook, string>({
            query: bookId => ({
                url: `${host}ar-playbook/business-users/books/${bookId}`,
                method: 'GET',
            }),
        }),
        /**
         * Lightweight endpoint for book editor - returns book metadata without full page content
         * Reduces HTTP payload by ~80-90% compared to getBook
         * Used when loading book for editing (avoids transferring all media URLs)
         */
        getBookMetadata: builder.query<IARPlaybook, string>({
            query: bookId => ({
                url: `${host}ar-playbook/business-users/books/${bookId}?fields=name,slug,categories.name,isPublished,authMethod,audioRepeatCount,pricing,demoSettings,ctaSettings,description,welcomeTagline,coverImage,featuredImage,targetAge,tags`,
                method: 'GET',
            }),
        }),
        updateBook: builder.mutation<IARPlaybook, Partial<IARPlaybook> & { bookId: string }>({
            query: ({ bookId, ...body }) => ({
                url: `${host}ar-playbook/business-users/books/${bookId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Books'],
        }),
        deleteBook: builder.mutation<IARPlaybook, { bookId: string }>({
            query: ({ bookId }) => ({
                url: `${host}ar-playbook/business-users/books/${bookId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Books'],
        }),
        createLicense: builder.mutation<string, { quantity: number; bookId: string }>({
            query: body => ({
                url: `${host}ar-playbook/business-users/license`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['BusinessUser'],
        }),
        downloadUnassignedLicenses: builder.mutation<
            IDownloadLicensesResponse,
            IDownloadLicensesRequest
        >({
            query: body => ({
                url: `${host}ar-playbook/business-users/license/download/unassigned`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['BusinessUser'],
        }),
        downloadPendingLicenses: builder.mutation<
            IDownloadLicensesResponse,
            IDownloadLicensesRequest
        >({
            query: body => ({
                url: `${host}ar-playbook/business-users/license/download/pending`,
                method: 'POST',
                body,
            }),
        }),

        // Custom Domain Endpoints
        getCustomDomains: builder.query<ICustomDomainsListResponse, void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/custom-domains`,
                method: 'GET',
            }),
            providesTags: ['CustomDomains'],
        }),
        addCustomDomain: builder.mutation<ICustomDomainResponse, { domain: string }>({
            query: body => ({
                url: `${host}ar-playbook/business-users/custom-domains`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CustomDomains'],
        }),
        deleteCustomDomain: builder.mutation<{ success: boolean }, string>({
            query: domainId => ({
                url: `${host}ar-playbook/business-users/custom-domains/${domainId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['CustomDomains'],
        }),
        verifyCustomDomain: builder.mutation<IVerifyDomainResponse, string>({
            query: domainId => ({
                url: `${host}ar-playbook/business-users/custom-domains/${domainId}/verify`,
                method: 'POST',
            }),
            invalidatesTags: ['CustomDomains'],
        }),
        getDNSInstructions: builder.query<
            {
                success: boolean
                data: { domain: string; status: string; instructions: IDNSInstructions }
            },
            string
        >({
            query: domainId => ({
                url: `${host}ar-playbook/business-users/custom-domains/${domainId}/instructions`,
                method: 'GET',
            }),
        }),
        refreshDomainStatus: builder.mutation<IVerifyDomainResponse, string>({
            query: domainId => ({
                url: `${host}ar-playbook/business-users/custom-domains/${domainId}/refresh`,
                method: 'POST',
            }),
            invalidatesTags: ['CustomDomains'],
        }),
        // Analytics
        getAnalytics: builder.query<IAREducationAnalytics, IAnalyticsQuery | void>({
            query: (params = {}) => {
                const queryParams = new URLSearchParams()
                if (params && 'startDate' in params && params.startDate) {
                    queryParams.append('startDate', params.startDate)
                }
                if (params && 'endDate' in params && params.endDate) {
                    queryParams.append('endDate', params.endDate)
                }
                if (params && 'bookId' in params && params.bookId) {
                    queryParams.append('bookId', params.bookId)
                }
                const queryString = queryParams.toString()
                return {
                    url: `${host}ar-playbook/business-users/analytics${queryString ? `?${queryString}` : ''}`,
                    method: 'GET',
                }
            },
        }),

        // ============================================
        // Marketplace Seller Endpoints
        // ============================================

        /**
         * Record an offline sale (cash payment)
         */
        recordOfflineSale: builder.mutation<IRecordOfflineSaleResponse, IRecordOfflineSaleRequest>({
            query: body => ({
                url: `${host}ar-playbook/business-users/marketplace/offline-sale`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['SellerEarnings', 'SellerSales'],
        }),

        /**
         * Get seller earnings summary
         */
        getSellerEarnings: builder.query<ISellerEarningsResponse, void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/marketplace/earnings`,
                method: 'GET',
            }),
            providesTags: ['SellerEarnings'],
        }),

        /**
         * Get seller sales list with pagination
         */
        getSellerSales: builder.query<ISellerSalesResponse, ISellerSalesQuery>({
            query: (params = {}) => {
                const queryParams = new URLSearchParams()
                if (params.page) {
                    queryParams.append('page', params.page.toString())
                }
                if (params.limit) {
                    queryParams.append('limit', params.limit.toString())
                }
                if (params.bookId) {
                    queryParams.append('bookId', params.bookId)
                }
                if (params.status) {
                    queryParams.append('status', params.status)
                }
                if (params.saleType) {
                    queryParams.append('saleType', params.saleType)
                }
                const queryString = queryParams.toString()
                return {
                    url: `${host}ar-playbook/business-users/marketplace/sales${queryString ? `?${queryString}` : ''}`,
                    method: 'GET',
                }
            },
            providesTags: ['SellerSales'],
        }),

        /**
         * Get marketplace settings for authenticated seller
         */
        getMarketplaceSettings: builder.query<IMarketplaceSettingsResponse, void>({
            query: () => ({
                url: `${host}ar-playbook/business-users/marketplace/settings`,
                method: 'GET',
            }),
            providesTags: ['MarketplaceSettings'],
        }),

        /**
         * Update marketplace settings for authenticated seller
         */
        updateMarketplaceSettings: builder.mutation<
            { settings: IMarketplaceSettingsResponse['settings'] },
            IUpdateMarketplaceSettingsRequest
        >({
            query: body => ({
                url: `${host}ar-playbook/business-users/marketplace/settings`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['MarketplaceSettings'],
        }),
    }),
})

export const {
    useGetAssetsLinkQuery,
    useLazyGetAssetsLinkQuery,
    useCreateFileMutation,
    useBusinessUserSignupMutation,
    useBusinessUserGoogleSignupMutation,
    useBusinessUserGoogleLoginMutation,
    useBusinessUserSetPasswordMutation,
    useCheckSubdomainAvailabilityMutation,
    useBusinessUserLoginMutation,
    useBusinessUserForgotPasswordMutation,
    useBusinessUserResetPasswordMutation,
    useBusinessUserProfileQuery,
    useLazyBusinessUserProfileQuery,
    useGetThemeQuery,
    useUpdateThemeMutation,
    useUpdateAuthSettingsMutation,
    useUpdateCookieConsentSettingMutation,
    useGetModelAssetsQuery,
    // AI Audio hooks
    useGenerateAudioMutation,
    useGetAvailableVoicesQuery,
    useLazyGetAvailableVoicesQuery,
    // Book hooks
    useCreateBookMutation,
    useGetAllBooksQuery,
    useLazyGetAllBooksQuery,
    usePublishBookMutation,
    useGetBookQuery,
    useLazyGetBookQuery,
    useGetBookMetadataQuery,
    useLazyGetBookMetadataQuery,
    useUpdateBookMutation,
    useDeleteBookMutation,
    useCreateLicenseMutation,
    useDownloadUnassignedLicensesMutation,
    useDownloadPendingLicensesMutation,
    // Custom Domain hooks
    useGetCustomDomainsQuery,
    useAddCustomDomainMutation,
    useDeleteCustomDomainMutation,
    useVerifyCustomDomainMutation,
    useGetDNSInstructionsQuery,
    useLazyGetDNSInstructionsQuery,
    useRefreshDomainStatusMutation,
    // Analytics hooks
    useGetAnalyticsQuery,
    useLazyGetAnalyticsQuery,
    // Marketplace seller hooks
    useRecordOfflineSaleMutation,
    useGetSellerEarningsQuery,
    useLazyGetSellerEarningsQuery,
    useGetSellerSalesQuery,
    useLazyGetSellerSalesQuery,
    // Marketplace settings hooks
    useGetMarketplaceSettingsQuery,
    useUpdateMarketplaceSettingsMutation,
} = businessUserApi

export const studentApi = createApi({
    reducerPath: 'studentApi',
    baseQuery: studentBaseQueryWithAuth,
    refetchOnMountOrArgChange: 30,
    tagTypes: ['Student'],
    endpoints: builder => ({
        verifyUniquePhone: builder.mutation<string, { phone: number }>({
            query: data => ({
                url: 'ar-playbook/students/verify-unique-phone',
                method: 'POST',
                body: data,
            }),
        }),

        sendOtp: builder.mutation<void, ISendOTPRequest>({
            query: body => ({
                url: 'otp/send-otp',
                method: 'POST',
                body,
            }),
        }),

        verifyOtp: builder.mutation<void, IVerifyOTPRequest>({
            query: body => ({
                url: 'otp/verify-otp',
                method: 'POST',
                body,
            }),
        }),

        registerStudent: builder.mutation<ILicenseResponse, IRegisterStudentRequest>({
            query: body => ({
                url: 'ar-playbook/students/signup',
                method: 'POST',
                body,
            }),
        }),

        loginStudent: builder.mutation<ILicenseResponse, ILoginStudentRequest>({
            query: body => ({
                url: 'ar-playbook/students/login',
                method: 'POST',
                body,
            }),
        }),

        getBook: builder.mutation<IStudentBook, void>({
            query: body => ({
                url: 'ar-playbook/students/book',
                method: 'POST',
                body,
            }),
        }),
    }),
})

export const {
    useVerifyUniquePhoneMutation,
    useSendOtpMutation,
    useVerifyOtpMutation,
    useRegisterStudentMutation,
    useLoginStudentMutation,
    useGetBookMutation,
} = studentApi

export const store = configureStore({
    reducer: {
        [businessUserApi.reducerPath]: businessUserApi.reducer,
        [studentApi.reducerPath]: studentApi.reducer,
        [arPlaybookAdminApi.reducerPath]: arPlaybookAdminApi.reducer,
        [arPlaybookSuperAdminApi.reducerPath]: arPlaybookSuperAdminApi.reducer,
        [marketplaceApi.reducerPath]: marketplaceApi.reducer,
    },
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware()
            .concat(businessUserApi.middleware)
            .concat(studentApi.middleware)
            .concat(arPlaybookAdminApi.middleware)
            .concat(arPlaybookSuperAdminApi.middleware)
            .concat(marketplaceApi.middleware),
})
